import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Check } from "lucide-react";

import { useAuth, formatApiError } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Brand from "@/components/Brand";

// Self-serve owner signup — DaxOps-style. A business owner signs up in
// under a minute, gets their own private tenant (mode="owner"), and lands
// on the portal Home with the reduced Phase-1 feature allowlist:
// Snapshot + Can I claim + Can I afford + Debt Eradicator + Xero/Prosaic
// self-connect (Phase 1b). No firm required, no invite required.
export default function Signup() {
    const { signupOwner } = useAuth();
    const nav = useNavigate();
    const [search] = useSearchParams();
    // Public tools (like /tools/wealth-plan) send a `next` path so we land
    // the new owner back on the same tool with their inputs rehydrated.
    const nextPath = search.get("next") || "/portal";
    const planParam = search.get("plan");
    const [form, setForm] = useState({
        business_name: "",
        admin_name: "",
        admin_email: "",
        password: "",
        country: "NZ",
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    // When the email is already registered, we swap the raw error for a
    // friendly banner that includes a "Sign in instead" link. `alreadyRegistered`
    // is the offending email so we can pass it through to /login.
    const [alreadyRegistered, setAlreadyRegistered] = useState(null);

    function set(k, v) {
        setForm((f) => ({ ...f, [k]: v }));
        // Clear the "already registered" banner as soon as the user edits
        // any field — otherwise it lingers after they try a new email.
        if (alreadyRegistered) setAlreadyRegistered(null);
    }

    async function submit(e) {
        e?.preventDefault();
        setErr(null);
        setAlreadyRegistered(null);
        setBusy(true);
        try {
            const res = await signupOwner(form);
            toast.success(`Welcome to ClientOps, ${res.tenant.name}`);
            // Only honor `next` if it's a same-origin path (starts with /
            // but not // to prevent protocol-relative escape).
            const safeNext = nextPath.startsWith("/") && !nextPath.startsWith("//") ? nextPath : "/portal";
            const finalPath = planParam && safeNext.startsWith("/portal/") ? `${safeNext}?plan=${planParam}` : safeNext;
            nav(finalPath, { replace: true });
        } catch (e2) {
            const status = e2?.response?.status;
            const detail = e2?.response?.data?.detail;
            // Explicit "email already registered" → offer sign-in path.
            const isDuplicate = status === 400 && typeof detail === "string" && /already registered/i.test(detail);
            if (isDuplicate) {
                setAlreadyRegistered(form.admin_email.trim().toLowerCase());
            } else if (!e2?.response) {
                // Missing response = network/CORS/Cloudflare hiccup. Give the
                // user a clearer, actionable message instead of raw
                // "Network error" from axios.
                setErr("Couldn't reach the server just now. Check your connection and try again — your info is still filled in.");
            } else {
                setErr(formatApiError(e2));
            }
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="min-h-screen grid lg:grid-cols-2">
            <div className="flex items-center justify-center px-6 py-12">
                <div className="w-full max-w-md">
                    <Link to="/" className="mb-10 inline-flex items-center gap-2 text-xs text-[#5E636E] hover:text-[#0F1115] transition-colors" data-testid="signup-back-home">
                        <ArrowLeft className="h-3.5 w-3.5" /> Back to home
                    </Link>
                    <Brand size="md" className="mb-8" />
                    <h1 className="font-display text-3xl tracking-tight font-medium">Start free. Keep more of your income.</h1>
                    <p className="mt-2 text-sm text-[#5E636E]">
                        Your business snapshot, deductibility checks, and a debt-freedom date — all in one place.{" "}
                        <Link to="/login" className="text-[#171717] underline underline-offset-4" data-testid="signup-login-link">
                            Already have an account?
                        </Link>
                    </p>

                    <form className="mt-8 space-y-4" onSubmit={submit} data-testid="signup-form">
                        <div className="space-y-1.5">
                            <Label htmlFor="business_name">Business name</Label>
                            <Input
                                id="business_name"
                                placeholder="e.g. Harbour Joinery Ltd"
                                value={form.business_name}
                                onChange={(e) => set("business_name", e.target.value)}
                                required
                                data-testid="signup-business-name"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="admin_name">Your name</Label>
                            <Input
                                id="admin_name"
                                placeholder="Jane Doe"
                                value={form.admin_name}
                                onChange={(e) => set("admin_name", e.target.value)}
                                required
                                data-testid="signup-admin-name"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="admin_email">Email</Label>
                            <Input
                                id="admin_email"
                                type="email"
                                placeholder="you@example.co.nz"
                                value={form.admin_email}
                                onChange={(e) => set("admin_email", e.target.value)}
                                required
                                data-testid="signup-admin-email"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="password">Password</Label>
                            <Input
                                id="password"
                                type="password"
                                placeholder="Minimum 8 characters"
                                minLength={8}
                                value={form.password}
                                onChange={(e) => set("password", e.target.value)}
                                required
                                data-testid="signup-password"
                            />
                        </div>
                        {alreadyRegistered && (
                            <div
                                className="rounded-md border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900"
                                data-testid="signup-already-registered"
                            >
                                <div className="font-medium">You&apos;re already signed up</div>
                                <p className="mt-1 text-[13px] text-amber-800 leading-relaxed">
                                    <span className="font-mono">{alreadyRegistered}</span> is already registered.
                                </p>
                                <Link
                                    to={`/login?email=${encodeURIComponent(alreadyRegistered)}`}
                                    className="mt-2 inline-flex items-center gap-1 text-[13px] font-medium text-amber-900 underline underline-offset-4"
                                    data-testid="signup-already-registered-signin"
                                >
                                    Sign in instead →
                                </Link>
                            </div>
                        )}
                        {err && !alreadyRegistered && (
                            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700" data-testid="signup-error">
                                {err}
                            </div>
                        )}
                        <Button
                            type="submit"
                            className="w-full h-10 bg-[#171717] hover:bg-[#2E2E2E] text-white"
                            disabled={busy}
                            data-testid="signup-submit-button"
                        >
                            {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Creating your account…</> : "Start free"}
                        </Button>
                        <p className="text-[11px] text-[#5E636E]">
                            No credit card. No accountant required. Cancel anytime.
                        </p>
                        <p className="text-[11px] text-[#5E636E]">
                            Are you an accounting firm?{" "}
                            <Link to="/signup/firm" className="underline underline-offset-4 text-[#171717]" data-testid="signup-firm-link">
                                Sign up as a firm instead →
                            </Link>
                        </p>
                    </form>
                </div>
            </div>
            <div className="hidden lg:flex bg-[#0F1115] text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-dot-grid opacity-[0.08]" />
                <div className="relative z-10 m-auto max-w-md px-12">
                    <div className="co-label !text-white/60 mb-3">What you get, free</div>
                    <ul className="space-y-4 text-sm text-white/80 leading-relaxed">
                        <li className="flex items-start gap-3">
                            <Check className="h-4 w-4 mt-0.5 text-emerald-400 shrink-0" />
                            <div><span className="font-display text-white text-base font-medium block">Your business at a glance</span> Snapshot of what you have, what you owe, what&apos;s next.</div>
                        </li>
                        <li className="flex items-start gap-3">
                            <Check className="h-4 w-4 mt-0.5 text-emerald-400 shrink-0" />
                            <div><span className="font-display text-white text-base font-medium block">Can I claim it?</span> Snap a receipt — get a plain-English &quot;yes / no / partial&quot; in seconds.</div>
                        </li>
                        <li className="flex items-start gap-3">
                            <Check className="h-4 w-4 mt-0.5 text-emerald-400 shrink-0" />
                            <div><span className="font-display text-white text-base font-medium block">Can I afford it?</span> Simulate a purchase against your real cashflow before you spend.</div>
                        </li>
                        <li className="flex items-start gap-3">
                            <Check className="h-4 w-4 mt-0.5 text-emerald-400 shrink-0" />
                            <div><span className="font-display text-white text-base font-medium block">Become debt-free earlier</span> Track every debt and see the exact date you&apos;ll be free.</div>
                        </li>
                        <li className="flex items-start gap-3">
                            <Check className="h-4 w-4 mt-0.5 text-emerald-400 shrink-0" />
                            <div><span className="font-display text-white text-base font-medium block">Connect your own Xero or Prosaic</span> Bring your own accounting data. Your business stays yours.</div>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    );
}
